/** Divorce navigator client data: static registry (baked for prerender) + helpers. */

import registryJson from './registry.json';

export type ChecklistStep = {
  id: string;
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
};

export type DivorceJurisdiction = {
  code: string;
  slug: string;
  name: string;
  residency_summary: string;
  waiting_period_summary: string;
  filing_fee_min_cents: number;
  filing_fee_max_cents: number;
  fee_waiver_available: boolean;
  fee_waiver_form_url: string | null;
  official_forms_url: string;
  official_efile_url: string | null;
  self_help_url: string | null;
  notes: string | null;
  checklist?: ChecklistStep[] | null;
};

export type ApprovedForm = {
  form_number: string | null;
  title: string;
  revision_label: string | null;
  source_url: string;
  purpose: string | null;
};

export const DIVORCE_JURISDICTIONS = registryJson as DivorceJurisdiction[];

const BY_SLUG = new Map(DIVORCE_JURISDICTIONS.map((j) => [j.slug, j]));

export function isDivorceStateSlug(slug: string): boolean {
  return BY_SLUG.has(slug);
}

export function getDivorceJurisdictionBySlug(
  slug: string,
): DivorceJurisdiction | undefined {
  return BY_SLUG.get(slug);
}

export function formatFeeRange(j: DivorceJurisdiction): string {
  const min = Math.round(j.filing_fee_min_cents / 100);
  const max = Math.round(j.filing_fee_max_cents / 100);
  return min === max ? `$${min}` : `$${min}–$${max}`;
}

/** Default Tier A checklist derived from registry fields (counsel-reviewed copy source). */
export function buildChecklist(j: DivorceJurisdiction): ChecklistStep[] {
  if (j.checklist && j.checklist.length > 0) {
    return j.checklist;
  }
  const steps: ChecklistStep[] = [
    {
      id: 'residency',
      title: 'Confirm you can file in ' + j.name,
      body: j.residency_summary,
    },
    {
      id: 'forms',
      title: 'Get the official forms',
      body: `${j.name} publishes its own court forms — blank court forms are public documents and always free.`,
      linkUrl: j.official_forms_url,
      linkLabel: `Official ${j.name} forms`,
    },
    {
      id: 'fees',
      title: `Filing fee: ${formatFeeRange(j)}`,
      body: j.fee_waiver_available
        ? 'Paid to the court when you file. If you can’t afford it, you can ask the court to waive the fee based on income.'
        : 'Paid to the court when you file.',
      ...(j.fee_waiver_form_url
        ? { linkUrl: j.fee_waiver_form_url, linkLabel: 'Fee waiver information' }
        : {}),
    },
    {
      id: 'file',
      title: 'File with the court',
      body: j.official_efile_url
        ? `${j.name} supports electronic filing for self-represented filers, or you can file in person with the clerk.`
        : 'Take your completed forms to the clerk of the court in the county where you meet residency requirements.',
      ...(j.official_efile_url
        ? { linkUrl: j.official_efile_url, linkLabel: 'Official e-filing' }
        : {}),
    },
    {
      id: 'serve',
      title: 'Serve your spouse',
      body: 'After filing, your spouse must be formally notified ("served"). Many uncontested cases use a signed waiver or acceptance of service instead of a process server.',
    },
    {
      id: 'wait',
      title: 'Waiting period',
      body: j.waiting_period_summary,
    },
    {
      id: 'judgment',
      title: 'Final judgment',
      body: 'Submit your final paperwork (including your settlement and parenting plan if you have children) and the court issues your judgment.',
    },
  ];
  return steps;
}
